import LaunchIconRounded from "@mui/icons-material/Launch";
import { Box, Chip, Divider, Grid, Typography } from "@mui/material";
import useAPI from "../../../../api/hooks/useAPI";
import useColors from "../../../../api/hooks/useColors";
import CustomLabel from "../../frameworks/components/CustomLabel";

const sharedStyles = {
  padding: "6px",
  borderRadius: "6px",
  display: "inline-flex",
  alignItems: "center",
  fontFamily: "Poppins",
  fontSize: "12px",
  fontWeight: 400,
  lineHeight: "16px",
  height: "18px",
};

export const ThreatTypeChips = ({
  items,
  justifyContent = "center",
  sx = {},
}) => {
  const colors = useColors();
  const { trackEvent } = useAPI();

  const onThreatTypeClick = (item) => {
    trackEvent(
      "Clicked on Threat Type: " + item + " - it's not clickable as of now"
    );
  };

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2, ...sx }}>
      <Typography variant="subtitle2">Threat Type</Typography>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: justifyContent,
          gap: 1,
        }}
      >
        {items.map((item, index) => (
          <CustomLabel
            text={item}
            colorFamily="secondary"
            severity="dark"
            labelType="soft"
            size="small"
            onClick={() => onThreatTypeClick(item)}
          />
        ))}
      </Box>
    </Box>
  );
};

export const ThreatActorChips = ({ items, justifyContent = "center" }) => {
  const { trackEvent } = useAPI();
  const colors = useColors();

  const openAttackGroupeUrl = (item) => {
    trackEvent("Clicked on MITRE ATT&CK Actor: " + item.name);
    window.open(
      "https://attack.mitre.org/groups/" + item.external_id,
      "_blank"
    );
  };
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="subtitle2">Threat Actors</Typography>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: justifyContent,
          gap: 1,
        }}
      >
        {items.map((item, index) => (
          <CustomLabel
            text={item.name}
            colorFamily="secondary"
            severity="dark"
            labelType="soft"
            size="small"
            onClick={() => openAttackGroupeUrl(item)}
          />
        ))}
      </Box>
    </Box>
  );
};

export const AttackTechniqueChips = ({ items }) => {
  const { trackEvent } = useAPI();
  const colors = useColors();

  const openTechniqueUrl = (item) => {
    trackEvent("Clicked on MITRE ATT&CK Technique: " + item);
    const formattedItem = item.includes(".") ? item.replace(".", "/") : item;
    window.open(
      `https://attack.mitre.org/techniques/${formattedItem}`,
      "_blank"
    );
  };
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
      <Typography variant="subtitle2">Attack Techniques</Typography>
      <Box
        sx={{
          display: "flex",
          flexWrap: "wrap",
          justifyContent: "flex-start",
          gap: 1,
        }}
      >
        {items.map((item, index) => (
          <Chip
            key={index}
            label={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <span>{item}</span>
                <LaunchIconRounded sx={{ width: 16, height: 16 }} />
              </Box>
            }
            clickable={true}
            onClick={() => openTechniqueUrl(item)}
            sx={{
              ...sharedStyles,
              background: colors.secondary_8percent,
              color: colors.secondary_darker,
              "&:hover": {
                background: colors.secondary_12percent,
              },
            }}
          />
        ))}
      </Box>
    </Box>
  );
};

const ThreatIntel = ({ step, showTechniqueId }) => {
  const gridSize = showTechniqueId ? 3.5 : 5.5;

  return (
    <Grid
      container
      mt="24px"
      sx={{ textAlign: "center", justifyContent: "space-between" }}
    >
      <Grid item xs={gridSize} key="ThreatType">
        <Box>
          <ThreatTypeChips items={step.possible_threats} />
        </Box>
      </Grid>
      <Divider orientation="vertical" flexItem />
      <Grid item xs={gridSize} key="ThreatActors">
        <Box>
          {/* TODO Decide how many attack groups to show */}
          <ThreatActorChips items={step.mitre_attack_groups?.slice(0, 5)} />
        </Box>
      </Grid>
      {showTechniqueId && (
        <>
          <Divider orientation="vertical" flexItem />
          <Grid item xs={gridSize} key="AttackTechniques">
            <Box>
              <AttackTechniqueChips
                items={step.mitre_attack_techniques?.slice(0, 5)}
              />
            </Box>
          </Grid>
        </>
      )}
    </Grid>
  );
};

export default ThreatIntel;
